import { useState, useMemo, useEffect } from "react";
import {
  Icon,
  Input,
  Badge,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@elaniintc/goes-design-system";
import type { Revision } from "@/queries/formaly-request";
import { cn } from "@/utils/theme";

interface RevisionGroup {
  id: string;
  name: string;
}

interface RevisionWithGroups extends Revision {
  groups?: RevisionGroup[];
}

interface RevisionWithNumber extends RevisionWithGroups {
  number: number;
}

/** Maps revision ID -> Set of group IDs that have pending VoBos */
export interface PendingVoBoMap {
  /** revision IDs that have at least one pending VoBo */
  revisions: Set<string>;
  /** group IDs that have at least one pending VoBo (across all revisions) */
  groups: Set<string>;
}

interface RevisionSelectorProps {
  revisions: RevisionWithGroups[];
  selectedRevisionId?: string;
  selectedGroupId?: string;
  onSelect: (revisionId: string, groupId?: string) => void;
  pendingVoBo?: PendingVoBoMap;
  shouldDisabledDropdown?: boolean;
}

export const RevisionSelector = ({
  revisions,
  selectedRevisionId,
  selectedGroupId,
  onSelect,
  pendingVoBo,
  shouldDisabledDropdown = false,
}: RevisionSelectorProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRevisionForGroups, setSelectedRevisionForGroups] = useState<
    string | null
  >(null);
  const [isOpen, setIsOpen] = useState(false);

  // Ordenar revisiones por fecha de creación y asignar números
  const sortedRevisionsWithNumbers = useMemo(() => {
    const sorted = [...revisions].sort((a, b) => {
      const dateA = new Date(a.created_at || 0).getTime();
      const dateB = new Date(b.created_at || 0).getTime();
      return dateA - dateB;
    });
    return sorted.map((rev, index) => ({
      ...rev,
      number: index + 1,
    }));
  }, [revisions]);

  // Filtrar revisiones por búsqueda
  const filteredRevisions = useMemo(() => {
    if (!searchQuery.trim()) return sortedRevisionsWithNumbers;

    const query = searchQuery.toLowerCase();
    return sortedRevisionsWithNumbers.filter((revision) => {
      const matchesNumber = `revisión #${revision.number}`
        .toLowerCase()
        .includes(query);
      const matchesGroups = revision.groups?.some((group) =>
        group.name.toLowerCase().includes(query),
      );
      return matchesNumber || matchesGroups;
    });
  }, [sortedRevisionsWithNumbers, searchQuery]);

  // Obtener los grupos de una revisión específica
  // Los grupos ya vienen filtrados desde el componente padre para mostrar solo los del usuario
  const getUserGroupsForRevision = (
    revision: RevisionWithNumber | RevisionWithGroups,
  ) => {
    if (!revision.groups || revision.groups.length === 0) return [];
    return revision.groups;
  };

  // Determinar si una revisión tiene múltiples grupos
  const hasMultipleUserGroups = (
    revision: RevisionWithNumber | RevisionWithGroups,
  ) => {
    const userGroups = getUserGroupsForRevision(revision);
    return userGroups.length > 1;
  };

  const handleRevisionClick = (revision: RevisionWithNumber) => {
    if (hasMultipleUserGroups(revision)) {
      // Mostrar lista de grupos
      setSelectedRevisionForGroups(revision.id);
    } else {
      // Seleccionar directamente la revisión
      const groups = getUserGroupsForRevision(revision);
      const groupId = groups.length > 0 ? groups[0].id : undefined;
      onSelect(revision.id, groupId);
      setIsOpen(false);
      setSelectedRevisionForGroups(null);
    }
  };

  const handleGroupClick = (groupId: string) => {
    if (selectedRevisionForGroups) {
      onSelect(selectedRevisionForGroups, groupId);
      setIsOpen(false);
      setSelectedRevisionForGroups(null);
    }
  };

  const handleBackToRevisions = () => {
    setSelectedRevisionForGroups(null);
  };

  const selectedRevision = revisions.find(
    (r) => r.id === (selectedRevisionForGroups || selectedRevisionId),
  );

  const selectedGroup = selectedRevision?.groups?.find(
    (g) => g.id === selectedGroupId,
  );

  const displayText = useMemo(() => {
    if (selectedRevisionId) {
      const rev = sortedRevisionsWithNumbers.find(
        (r) => r.id === selectedRevisionId,
      );
      if (rev) {
        const revText = `Revisión #${rev.number}`;
        if (selectedGroupId && selectedGroup) {
          return `${revText}, Grupo: ${selectedGroup.name}`;
        }
        // Si tiene grupos pero no hay selectedGroupId, mostrar el primer grupo
        if (rev.groups && rev.groups.length > 0) {
          return `${revText}, Grupo: ${rev.groups[0].name}`;
        }
        return revText;
      }
    }
    return "Seleccionar revisión";
  }, [
    selectedRevisionId,
    selectedGroupId,
    sortedRevisionsWithNumbers,
    selectedGroup,
  ]);

  useEffect(() => {
    if (!selectedRevisionId) {
      // if not selected, select the latest revision by default
      const latestRevision =
        sortedRevisionsWithNumbers[sortedRevisionsWithNumbers.length - 1];
      if (latestRevision) {
        const groups = getUserGroupsForRevision(latestRevision);
        const groupId = groups.length > 0 ? groups[0].id : undefined;
        onSelect(latestRevision.id, groupId);
      }
    }
  }, [
    filteredRevisions,
    selectedRevisionId,
    onSelect,
    sortedRevisionsWithNumbers,
  ]);

  if (shouldDisabledDropdown) {
    return null;
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <button className="bg-neutral-object border border-neutral rounded-lg flex items-center gap-3 px-2 py-1 hover:bg-neutral-object-accent-silent transition-colors">
          <Badge
            shape="pill"
            variant="action"
            className="size-6 rounded-full flex items-center justify-center"
          >
            <Icon type="fas" name="file-edit" className="text-xs" />
          </Badge>
          <span className="text-body-loud text-sm flex-1 truncate max-w-[200px]">
            {displayText}
          </span>
          <Icon
            type="far"
            name="angle-down"
            className="text-body text-sm shrink-0"
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-80 rounded-lg border-neutral p-0 shadow-xl"
        align="start"
      >
        {/* Search bar */}
        <div className="p-3 border-b border-neutral-quiet">
          <div className="relative">
            <Icon
              type="far"
              name="search"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-body"
            />
            <Input
              placeholder="Buscar..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10"
            />
          </div>
        </div>

        {/* Content */}
        <div className="max-h-[440px] overflow-y-auto p-2">
          {!selectedRevisionForGroups ? (
            <div className="flex flex-col">
              {filteredRevisions.length === 0 ? (
                <div className="text-center text-body-quiet py-8 text-sm">
                  No se encontraron revisiones
                </div>
              ) : (
                filteredRevisions.map((revision) => {
                  const hasGroups = hasMultipleUserGroups(revision);
                  const isSelected = revision.id === selectedRevisionId;
                  const hasVoBo = pendingVoBo?.revisions.has(revision.id);
                  return (
                    <DropdownMenuItem
                      key={revision.id}
                      onSelect={(e) => {
                        // Prevent closing if it has groups (submenu)
                        if (hasGroups) {
                          e.preventDefault();
                        }
                        handleRevisionClick(revision);
                      }}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer",
                        isSelected && "bg-action-accent-silent",
                      )}
                    >
                      <div className="bg-action-accent-quiet border-[0.5px] border-action-quiet rounded-full size-6 flex items-center justify-center shrink-0">
                        <Icon
                          type="fas"
                          name="file-edit"
                          className="text-action-quiet text-xs"
                        />
                      </div>
                      <span className="flex-1 text-body text-base truncate">
                        Revisión #{revision.number}
                      </span>
                      {hasVoBo && (
                        <Badge
                          variant="warning"
                          className="text-xs px-2 py-0.5 shrink-0"
                        >
                          VoBo
                        </Badge>
                      )}
                      {isSelected && (
                        <Badge
                          variant="action"
                          className="text-xs px-2 py-0.5 shrink-0"
                        >
                          Actual
                        </Badge>
                      )}
                      {hasGroups && (
                        <Icon
                          type="far"
                          name="angle-right"
                          className="text-body text-base shrink-0"
                        />
                      )}
                    </DropdownMenuItem>
                  );
                })
              )}
            </div>
          ) : (
            // Lista de grupos para la revisión seleccionada
            <div className="flex flex-col">
              {/* Breadcrumb */}
              <button
                onClick={handleBackToRevisions}
                className="flex items-center gap-2 px-3 py-2 text-xs text-body border-b border-neutral-quiet mb-2 hover:bg-neutral-object-accent-silent"
              >
                <div className="flex items-center gap-2">
                  <Icon
                    type="fas"
                    name="file-edit"
                    className="text-action-quiet text-xs"
                  />
                  <span className="font-semibold">
                    Revisión #
                    {
                      sortedRevisionsWithNumbers.find(
                        (r) => r.id === selectedRevision?.id,
                      )?.number
                    }
                  </span>
                  <Icon
                    type="far"
                    name="angle-right"
                    className="text-body-quiet"
                  />
                  <span className="text-body-quiet">Grupos</span>
                </div>
                <div className="flex-1 h-px bg-neutral-quiet" />
              </button>

              {/* Lista de grupos */}
              {selectedRevision?.groups?.map((group) => {
                const isSelected = group.id === selectedGroupId;
                const hasVoBo = pendingVoBo?.groups.has(group.id);
                return (
                  <DropdownMenuItem
                    key={group.id}
                    onClick={() => handleGroupClick(group.id)}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer",
                      isSelected && "bg-action-accent-silent",
                    )}
                  >
                    <Icon
                      type="far"
                      name="users"
                      className="text-body text-base shrink-0"
                    />
                    <span className="flex-1 text-body text-base truncate">
                      {group.name}
                    </span>
                    {isSelected && (
                      <Badge
                        variant="action"
                        className="text-xs px-2 py-0.5 shrink-0"
                      >
                        Actual
                      </Badge>
                    )}
                    {hasVoBo && (
                      <Badge
                        variant="warning"
                        className="text-xs px-2 py-0.5 shrink-0"
                      >
                        VoBo
                      </Badge>
                    )}
                  </DropdownMenuItem>
                );
              })}
            </div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
